import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ModalShellProps {
  isOpen: boolean;
  onClose: () => void;
  size?: 'sm' | 'md' | 'lg';
  danger?: boolean;
  children: React.ReactNode;
}

const ModalShell: React.FC<ModalShellProps> = ({ isOpen, onClose, size = 'lg', danger = false, children }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      window.scrollTo(0, scrollY);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && modalRef.current) {
      const el = modalRef.current.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') as HTMLElement;
      el?.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClass = size === 'sm' ? 'sm' : size === 'md' ? 'md' : '';

  // Use portal so position:fixed covers full viewport even inside overflow:auto (iOS Safari)
  return ReactDOM.createPortal(
    <div
      ref={overlayRef}
      className="modal-backdrop-v2"
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      style={{ zIndex: 2000 }}
    >
      <div
        ref={modalRef}
        className={`modal-v2 ${sizeClass} ${danger ? 'danger' : ''}`}
        onClick={(e) => e.stopPropagation()}
        style={sizeClass ? undefined : { width: 'calc(100% - 32px)' }}
      >
        {children}
      </div>
    </div>,
    document.body
  );
};

interface ModalHeaderProps {
  tag?: React.ReactNode;
  title: string;
  subtitle?: React.ReactNode;
  onClose: () => void;
  icon?: React.ReactNode;
}

export const ModalHeader: React.FC<ModalHeaderProps> = ({ tag, title, subtitle, onClose, icon }) => {
  const { t } = useTranslation();
  return (
    <div className="modal-v2-hd">
      {icon && <div className="icon-cap">{icon}</div>}
      <div style={{ flex: 1, minWidth: 0 }}>
        {tag && <div className="tag">{tag}</div>}
        <h2 style={{ fontSize: 'clamp(15px, 4vw, 22px)' }}>{title}</h2>
        {subtitle && <div className="sub">{subtitle}</div>}
      </div>
      <button
        className="close"
        onClick={onClose}
        aria-label={t('common.close')}
        style={{ minWidth: 44, minHeight: 44, touchAction: 'manipulation' }}
      >
        <X size={16} />
      </button>
    </div>
  );
};

interface ModalBodyProps {
  split?: boolean;
  // Single-column body padding (.modal-v2-bd.single in index.css) — pass this
  // instead of wrapping children in <div className="single"> yourself; that
  // inner-div pattern never matched the CSS (it's a compound selector on
  // .modal-v2-bd itself) and silently left the body with zero padding.
  single?: boolean;
  children: React.ReactNode;
}

export const ModalBody: React.FC<ModalBodyProps> = ({ split = false, single = false, children }) => (
  <div className={`modal-v2-bd ${split ? 'split' : ''} ${single ? 'single' : ''}`}>
    {children}
  </div>
);

export const ModalFooter: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="modal-v2-ft">
    {children}
  </div>
);

export default ModalShell;
